import { useState } from 'react';
import {
    Card, CardContent
} from '@/components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Loader2, Pencil, Search, PackageSearch } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getReviewsByOrderNumber, updateReview } from '@/api/reviews.api';
import RatingStars from './RatingStars';
import EditReviewDialog from './EditReviewDialog';

// Order-wise rating lookup: type in an order number (full or partial) and
// see every review tied to matching orders. Distinct from "All Reviews",
// which browses/filters the whole review feed — this tab is for "did this
// specific order get rated, and what did the customer say?"
const ByOrderTab = () => {
    const [orderNumber, setOrderNumber] = useState('');
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reviews, setReviews] = useState([]);

    // ---- Edit / status (Super Admin) ----
    const [editing, setEditing] = useState(null);
    const [statusSavingId, setStatusSavingId] = useState(null);

    const replaceReview = (updated) => {
        if (!updated?.id) return;
        setReviews((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    };

    const toggleStatus = async (r) => {
        const next = r.status === 'INACTIVE' ? 'ACTIVE' : 'INACTIVE';
        setStatusSavingId(r.id);
        replaceReview({ ...r, status: next }); // optimistic
        try {
            const res = await updateReview(r.id, { status: next });
            replaceReview(res.data?.data);
            toast.success(next === 'ACTIVE' ? 'Review is now active' : 'Review hidden (inactive)');
        } catch (err) {
            replaceReview(r); // roll back
            toast.error(err.response?.data?.detail || 'Failed to update status');
        } finally {
            setStatusSavingId(null);
        }
    };

    const runSearch = async () => {
        const q = orderNumber.trim();
        if (!q) {
            toast.error('Enter an order number to search');
            return;
        }
        try {
            setLoading(true);
            setSearched(true);
            const res = await getReviewsByOrderNumber(q, { limit: 50 });
            setReviews(res.data?.data || []);
            if (!res.data?.data?.length) {
                toast('No rated orders match that order number');
            }
        } catch (err) {
            toast.error('Failed to search reviews');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        placeholder="Search by order number..."
                        value={orderNumber}
                        onChange={(e) => setOrderNumber(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                        className="pl-9"
                        data-testid="reviews-by-order-input"
                    />
                </div>
                <Button onClick={runSearch} disabled={loading} data-testid="reviews-by-order-search">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                </Button>
            </div>

            {!searched && (
                <div className="rounded-lg border bg-white p-10 flex flex-col items-center text-center text-gray-500">
                    <PackageSearch size={32} className="mb-2 text-gray-400" />
                    <p>Search for an order number to see the rating and review left for it.</p>
                </div>
            )}

            {searched && loading && (
                <div className="flex justify-center items-center h-48">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            )}

            {searched && !loading && (
                <Card className="border-none shadow-none bg-transparent">
                    <CardContent className="px-0">
                        <div className="rounded-lg border bg-white overflow-hidden">
                            <Table data-testid="reviews-by-order-table">
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Order #</TableHead>
                                        <TableHead>Order Status</TableHead>
                                        <TableHead>Restaurant</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead>Rating</TableHead>
                                        <TableHead>Comment</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {reviews.length > 0 ? reviews.map((r) => (
                                        <TableRow
                                            key={r.id}
                                            data-testid={`review-by-order-row-${r.id}`}
                                            className={r.status === 'INACTIVE' ? 'bg-gray-50/80 opacity-70' : ''}
                                        >
                                            <TableCell className="font-mono text-sm font-semibold">
                                                {r.order?.orderNumber ? `#${r.order.orderNumber}` : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{r.order?.deliveryStatus || r.order?.status || '-'}</Badge>
                                            </TableCell>
                                            <TableCell className="text-sm">{r.resturant?.name || '-'}</TableCell>
                                            <TableCell className="text-sm">{r.user?.name || 'Customer'}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <RatingStars rating={r.rating} />
                                                    <Badge variant="outline">{r.rating}/5</Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600 max-w-[260px] truncate" title={r.comment}>
                                                {r.comment || <span className="text-gray-400 italic">No comment</span>}
                                            </TableCell>
                                            <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                                                {r.createdAt ? format(new Date(r.createdAt), 'dd MMM yyyy, hh:mm a') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={r.status !== 'INACTIVE'}
                                                        onCheckedChange={() => toggleStatus(r)}
                                                        disabled={statusSavingId === r.id}
                                                        aria-label={r.status !== 'INACTIVE' ? 'Deactivate review' : 'Activate review'}
                                                        data-testid={`review-status-toggle-${r.id}`}
                                                    />
                                                    <Badge
                                                        className={r.status !== 'INACTIVE'
                                                            ? 'bg-green-100 text-green-800 border-0'
                                                            : 'bg-gray-200 text-gray-700 border-0'}
                                                    >
                                                        {r.status !== 'INACTIVE' ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditing(r)}
                                                    data-testid={`review-edit-${r.id}`}
                                                >
                                                    <Pencil size={14} className="mr-1" />Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={9} className="text-center py-10 text-gray-500">
                                                No rated orders match "{orderNumber}"
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}

            <EditReviewDialog
                review={editing}
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                onSaved={replaceReview}
            />
        </div>
    );
};

export default ByOrderTab;
